import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, Loader2, ShieldCheck, Star } from "lucide-react";
import { consultApi, consultError } from "../lib/consult";

const inputCls = "w-full bg-bg0 border border-white/12 text-white px-3 py-3 text-sm outline-none focus:border-gold placeholder:text-zinc-600";

export const StoryForm = ({ bookingId, sensitive }) => {
    const [story, setStory] = useState(null);
    const [form, setForm] = useState({ rating: 5, title: "", body: "", use_my_name: false, city: "", age: "", consent: false });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        consultApi.get(`/me/bookings/${bookingId}/story`).then(({ data }) => setStory(data?.id ? data : null)).catch(() => {});
    }, [bookingId]);

    const submit = async () => {
        if (!form.consent) return toast.error("Please tick the consent box to share your story");
        setBusy(true);
        try {
            await consultApi.post(`/me/bookings/${bookingId}/story`, form);
            const { data } = await consultApi.get(`/me/bookings/${bookingId}/story`);
            setStory(data);
            toast.success("Thank you — our team will review your story before it goes live");
        } catch (e) { toast.error(consultError(e)); } finally { setBusy(false); }
    };

    if (story?.status === "approved") {
        return (
            <div className="border border-lime/40 bg-lime/[0.06] p-6" data-testid="story-published">
                <div className="flex items-center gap-2 font-display text-xl uppercase tracking-tight">
                    <CheckCircle2 className="w-4 h-4 text-lime" /> Your story is live
                </div>
                <p className="mt-3 text-sm text-zinc-300">
                    Published {story.is_anonymous ? "anonymously" : `as ${story.display_name}`}. Thank you for helping
                    others take the first step.
                </p>
            </div>
        );
    }

    if (story?.status === "pending") {
        return (
            <div className="border border-white/10 bg-bg1 p-6" data-testid="story-pending">
                <div className="flex items-center gap-2 font-display text-xl uppercase tracking-tight">
                    <Clock className="w-4 h-4 text-gold" /> Story submitted
                </div>
                <p className="mt-3 text-sm text-zinc-400">
                    Our team is reviewing it. It will be published only after review
                    {story.is_anonymous ? ", and it will stay anonymous." : "."}
                </p>
            </div>
        );
    }

    return (
        <div className="border border-white/10 bg-bg1 p-6" data-testid="story-form">
            <div className="font-display text-xl uppercase tracking-tight">Share your experience</div>
            <p className="mt-2 text-sm text-zinc-400">
                Optional, and published only with your consent.
                {sensitive && " Because of the sensitive nature of your care, your story will always be shown anonymously."}
            </p>

            <div className="mt-5 flex items-center gap-1.5" data-testid="story-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setForm((f) => ({ ...f, rating: n }))} data-testid={`story-rating-${n}`}>
                        <Star className={`w-6 h-6 ${n <= form.rating ? "text-gold fill-gold" : "text-white/20"}`} />
                    </button>
                ))}
            </div>

            <div className="mt-5 space-y-4">
                <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="A short headline (optional)" data-testid="story-title" className={inputCls} />
                <textarea rows={5} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                    placeholder="What changed for you? Please avoid naming medicines or promising results."
                    data-testid="story-body" className={inputCls} />
                <div className="grid sm:grid-cols-2 gap-4">
                    <input value={form.age} onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))}
                        placeholder="Age (optional)" data-testid="story-age" className={inputCls} />
                    <input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                        placeholder="City (optional)" data-testid="story-city" className={inputCls} />
                </div>

                {!sensitive && (
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" checked={form.use_my_name} data-testid="story-use-name"
                            onChange={(e) => setForm((f) => ({ ...f, use_my_name: e.target.checked }))}
                            className="mt-1 accent-[#E5A900] w-4 h-4" />
                        <span className="text-xs text-zinc-300">Show my first name. Leave unticked to stay anonymous.</span>
                    </label>
                )}

                <label className="flex items-start gap-3 p-4 border border-white/12 cursor-pointer">
                    <input type="checkbox" checked={form.consent} data-testid="story-consent"
                        onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                        className="mt-1 accent-[#E5A900] w-4 h-4" />
                    <span className="text-xs text-zinc-300 leading-relaxed">
                        I consent to ReHealthMe publishing this experience on its website and channels. I understand my
                        health details, reports and consultation notes are never published, that my identity stays
                        private unless I chose otherwise above, and that I can ask for it to be removed at any time.
                    </span>
                </label>

                <button onClick={submit} disabled={busy} data-testid="story-submit"
                    className="w-full btn-pill btn-primary justify-center">
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> SENDING…</> : "Share my story"}
                </button>
                <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                    <ShieldCheck className="w-3.5 h-3.5 text-lime" /> Reviewed by our team before anything is published.
                </div>
            </div>
        </div>
    );
};
